import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { capitalInvestmentService } from '../services/apiServices';

const KEYS = {
  all: ['capital-investments'] as const,
  list: () => [...KEYS.all, 'list'] as const,
  contracts: (id: number) => [...KEYS.all, id, 'contracts'] as const,
  payments: (id: number) => [...KEYS.all, id, 'payments'] as const,
};

export const useCapitalInvestments = () =>
  useQuery({ queryKey: KEYS.list(), queryFn: () => capitalInvestmentService.getAll() });

export const useCreateCapitalInvestment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => capitalInvestmentService.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.list() }),
  });
};

export const useUpdateCapitalInvestment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      capitalInvestmentService.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.list() }),
  });
};

export const useDeleteCapitalInvestment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => capitalInvestmentService.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.list() }),
  });
};

export const useInvestmentContracts = (id: number | null) =>
  useQuery({
    queryKey: KEYS.contracts(id!),
    queryFn: () => capitalInvestmentService.getContracts(id!),
    enabled: !!id,
  });

export const useAddInvestmentContract = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      capitalInvestmentService.addContract(id, data),
    onSuccess: (_res, { id }) => qc.invalidateQueries({ queryKey: KEYS.contracts(id) }),
  });
};

export const useUpdateInvestmentContract = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      investmentId,
      contractId,
      data,
    }: {
      investmentId: number;
      contractId: number;
      data: any;
    }) => capitalInvestmentService.updateContract(investmentId, contractId, data),
    onSuccess: (_res, { investmentId }) =>
      qc.invalidateQueries({ queryKey: KEYS.contracts(investmentId) }),
  });
};

export const useDeleteInvestmentContract = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ investmentId, contractId }: { investmentId: number; contractId: number }) =>
      capitalInvestmentService.deleteContract(investmentId, contractId),
    onSuccess: (_res, { investmentId }) =>
      qc.invalidateQueries({ queryKey: KEYS.contracts(investmentId) }),
  });
};

export const useInvestmentPayments = (id: number | null) =>
  useQuery({
    queryKey: KEYS.payments(id!),
    queryFn: () => capitalInvestmentService.getPayments(id!),
    enabled: !!id,
  });

export const useAddInvestmentPayment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      capitalInvestmentService.addPayment(id, data),
    onSuccess: (_res, { id }) => {
      qc.invalidateQueries({ queryKey: KEYS.payments(id) });
      qc.invalidateQueries({ queryKey: KEYS.contracts(id) });
      qc.invalidateQueries({ queryKey: KEYS.list() });
    },
  });
};

export const useUpdateInvestmentPayment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      investmentId,
      paymentId,
      data,
    }: {
      investmentId: number;
      paymentId: number;
      data: any;
    }) => capitalInvestmentService.updatePayment(investmentId, paymentId, data),
    onSuccess: (_res, { investmentId }) => {
      qc.invalidateQueries({ queryKey: KEYS.payments(investmentId) });
      qc.invalidateQueries({ queryKey: KEYS.contracts(investmentId) });
    },
  });
};

export const useDeleteInvestmentPayment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ investmentId, paymentId }: { investmentId: number; paymentId: number }) =>
      capitalInvestmentService.deletePayment(investmentId, paymentId),
    onSuccess: (_res, { investmentId }) => {
      qc.invalidateQueries({ queryKey: KEYS.payments(investmentId) });
      qc.invalidateQueries({ queryKey: KEYS.contracts(investmentId) });
      qc.invalidateQueries({ queryKey: KEYS.list() });
    },
  });
};

export const useCompleteInvestment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      capitalInvestmentService.complete(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.list() }),
  });
};
